import React, { useEffect, useState } from 'react';

/**
 * Style commun des contrôles (select) liés à l'année scolaire.
 * Objectif : un seul gabarit visuel pour les trois réglages d'année
 * (consultation, année courante, année d'inscription).
 */
export const schoolYearFieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  fontWeight: 600,
  color: '#1f2937',
  backgroundColor: '#fff',
  cursor: 'pointer',
  boxSizing: 'border-box',
};

interface SchoolYearCardProps {
  /** Emoji d'illustration affiché dans la pastille colorée. */
  icon: string;
  /** Fond de la pastille d'icône. */
  iconBg?: string;
  /** Couleur du titre (identifie la fonction du bloc). */
  titleColor?: string;
  /** Titre du bloc (fonction du réglage). */
  title: string;
  /** Phrase courte expliquant l'effet du réglage. */
  hint?: string;
  isRTL?: boolean;
  /** Libellé du champ principal (lien htmlFor vers `fieldId`). */
  fieldLabel?: string;
  /** Id du contrôle rendu dans `field` (accessibilité). */
  fieldId?: string;
  /** Contrôle principal (généralement un <select>). */
  field?: React.ReactNode;
  /** Badges / boutons alignés après le champ. */
  actions?: React.ReactNode;
  /** Contenu déplié sous la ligne principale (formulaire, aide…). */
  children?: React.ReactNode;
}

/**
 * Carte de réglage d'année scolaire — présentation homogène et responsive.
 * Le comportement (données, appels API) reste géré par les composants appelants.
 */
export const SchoolYearCard: React.FC<SchoolYearCardProps> = ({
  icon,
  iconBg = '#ede9fe',
  titleColor = '#4c1d95',
  title,
  hint,
  isRTL,
  fieldLabel,
  fieldId,
  field,
  actions,
  children,
}) => {
  const [windowWidth, setWindowWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = windowWidth < 768;
  // Si `isRTL` n'est pas fourni, on hérite de la direction du parent (comportement historique).
  const direction: React.CSSProperties['direction'] =
    isRTL === undefined ? undefined : isRTL ? 'rtl' : 'ltr';

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '14px',
        padding: isMobile ? '14px 16px' : '16px 20px',
        marginBottom: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        direction,
      }}
    >
      {/* Ligne principale : identité du réglage + contrôles */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: isMobile ? '12px' : '14px',
        }}
      >
        <span
          aria-hidden
          style={{
            width: '40px',
            height: '40px',
            flexShrink: 0,
            borderRadius: '12px',
            backgroundColor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '19px',
            lineHeight: 1,
          }}
        >
          {icon}
        </span>

        <div style={{ minWidth: 0, flex: '0 1 auto' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: titleColor, lineHeight: 1.35 }}>
            {title}
          </div>
          {hint && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', lineHeight: 1.4 }}>
              {hint}
            </div>
          )}
        </div>

        {(field || actions) && (
          <div
            style={{
              marginInlineStart: 'auto',
              display: 'flex',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
              flexWrap: 'wrap',
              gap: '10px',
              flex: isMobile ? '1 1 100%' : '0 1 auto',
            }}
          >
            {field && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: '8px',
                  flex: isMobile ? '1 1 100%' : '0 1 auto',
                  minWidth: 0,
                }}
              >
                {fieldLabel && (
                  <label
                    htmlFor={fieldId}
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#374151',
                      whiteSpace: 'nowrap',
                      cursor: fieldId ? 'pointer' : 'default',
                    }}
                  >
                    {fieldLabel}
                  </label>
                )}
                <div
                  style={{
                    display: 'flex',
                    flex: isMobile ? '1 1 auto' : '0 1 auto',
                    minWidth: isMobile ? 0 : '210px',
                    width: isMobile ? '100%' : undefined,
                  }}
                >
                  {field}
                </div>
              </div>
            )}

            {actions && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px',
                  flex: isMobile ? '1 1 100%' : '0 1 auto',
                }}
              >
                {actions}
              </div>
            )}
          </div>
        )}
      </div>

      {children}
    </div>
  );
};

/** Badge de statut compact (pastille + texte) réutilisé par les trois cartes. */
export const SchoolYearStatusChip: React.FC<{
  label: string;
  tone?: 'green' | 'amber' | 'slate';
  dot?: boolean;
}> = ({ label, tone = 'green', dot = true }) => {
  const tones = {
    green: { background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', dot: '#10b981' },
    amber: { background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d', dot: '#f59e0b' },
    slate: { background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', dot: '#94a3b8' },
  }[tone];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        fontWeight: 700,
        padding: '4px 10px',
        borderRadius: '999px',
        background: tones.background,
        color: tones.color,
        border: tones.border,
        whiteSpace: 'nowrap',
      }}
    >
      {dot && (
        <span
          style={{
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            background: tones.dot,
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </span>
  );
};
